import { convertToStartOfWeekUTC } from "~/utils/datetime"
import { cellKey, scopeToColumns } from "~/utils/demandScope"

// Offsets use ms arithmetic, not date-fns calendar arithmetic: that walks local wall-clock time and
// drifts an hour across DST, enough to push a week start past a window bound and drop the week.
export const DAY_MS = 24 * 60 * 60 * 1000
export const WEEK_MS = 7 * DAY_MS

// null on an axis = "all"; a leaf matches iff every non-null axis equals its value.
export type DemandScope = {
  channel: string | null
  warehouseId: string | null
  marketId: string | null
}

export type DemandAdjustmentIntent = {
  scope: DemandScope
  type: "add" | "percentage"
  value: number
  // setup folds into the base before the override; manual is an additive delta on that base
  // (moves one leaf without redistribution) and counts in the Manual net; overlay layers on
  // after. undefined ⇒ overlay, the DB default.
  stage?: "setup" | "manual" | "overlay"
  // Attribution identity (see `foldAdjustments`); absent ⇒ not attributed.
  key?: string
}

export type DemandOverrideIntent = {
  scope: DemandScope
  total: number
}

/**
 * A cell's share (0–1) of the variant's Manual total, so it moves with the total instead of
 * fixing units. The other free cells scale to the rest; the total never moves.
 */
export type DemandSharePinIntent = {
  scope: DemandScope
  share: number
}

// An accepted DS insight folded into the setup: a scoped additive contribution (frozen at accept).
export type SetupInsightIntent = {
  scope: DemandScope
  value: number
  /** See `DemandAdjustmentIntent.key`. */
  key?: string
  /**
   * `"variant"`: `value` is a whole-variant figure; each leaf in `scope` takes its share of the
   * VARIANT, so a sales-slice answer lands that slice's part of it. Default: shares of `scope`.
   */
  spread?: "variant"
}

export type BaselineLeaf = {
  warehouseId: string
  channel: string
  marketId: string
  level: number
  /**
   * Confirmed pre-orders the cell may not be planned below (`floorToConfirmed`). Absent ⇒ no
   * floor: not pre-order aware, or a manual forecast set it below its book.
   */
  preorderFloor?: number
  /**
   * The pin whose total this cell shares (absent: none). A floor only moves units inside one
   * group, or it would break that pin's total.
   */
  floorGroup?: string
  /**
   * The floor only moves units between its group's cells: a group planned under the sum of its
   * floors keeps its plan unfloored instead of being lifted to it.
   */
  floorKeepsTotal?: boolean
}

export type ProjectedLeaf = BaselineLeaf & {
  baseline: number
  projected: number
}

type LeafCell = Pick<BaselineLeaf, "channel" | "warehouseId" | "marketId">

export function leafMatchesScope(leaf: LeafCell, scope: DemandScope): boolean {
  if (scope.channel !== null && leaf.channel !== scope.channel) return false
  if (scope.warehouseId !== null && leaf.warehouseId !== scope.warehouseId) return false
  if (scope.marketId !== null && leaf.marketId !== scope.marketId) return false
  return true
}

/** An omitted collapse scope (and the all-"all" scope) keeps every leaf — the variant total. */
export function leafInScope(leaf: LeafCell, collapseScope?: DemandScope): boolean {
  return !collapseScope || leafMatchesScope(leaf, collapseScope)
}

/** Weekly-array primitives. Every series in the engine is one number per horizon week. */
export function addWeekly(dest: number[], src: number[]): void {
  for (let i = 0; i < dest.length; i++) dest[i] = (dest[i] ?? 0) + (src[i] ?? 0)
}

export function sumArray(arr: number[]): number {
  return arr.reduce((x, y) => x + y, 0)
}

// Uniform split when the pool has no weight, so a total never vanishes. Shared so overrides,
// additive and windowed adjustments all spread deltas the same way.
function shareOf(weight: number, denom: number, count: number): number {
  return denom > 0 ? weight / denom : 1 / Math.max(count, 1)
}

// `scopeShare`'s denominator, memoised per projection (inline it is O(leaves²) per adjustment).
// Keyed on scope object identity, which is stable for that life.
type ScopeWeights = (scope: DemandScope) => { total: number; count: number }

function scopeWeightsFor(leaves: BaselineLeaf[]): ScopeWeights {
  const memo = new Map<DemandScope, { total: number; count: number }>()
  return (scope) => {
    const hit = memo.get(scope)
    if (hit) return hit
    let total = 0
    let count = 0
    for (const l of leaves) {
      if (!leafMatchesScope(l, scope)) continue
      total += l.level
      count += 1
    }
    const weight = { total, count }
    memo.set(scope, weight)
    return weight
  }
}

// Shared with the dated-pin distribution so the two agree (else market-collapse would multiply).
function scopeShare(leaf: BaselineLeaf, scope: DemandScope, weights: ScopeWeights): number {
  const { total, count } = weights(scope)
  return shareOf(leaf.level, total, count)
}

// More constrained wins: primary = number of constrained (non-null) axes.
/** Set on every axis: exactly one leaf. */
const isLeafScope = (scope: DemandScope): boolean => scopeSpecificity(scope) === 3

function scopeSpecificity(scope: DemandScope): number {
  return (
    (scope.channel !== null ? 1 : 0) +
    (scope.warehouseId !== null ? 1 : 0) +
    (scope.marketId !== null ? 1 : 0)
  )
}

// Tiebreak between equally specific scopes: market > warehouse > channel.
function scopePriority(scope: DemandScope): number {
  return (
    (scope.marketId !== null ? 4 : 0) +
    (scope.warehouseId !== null ? 2 : 0) +
    (scope.channel !== null ? 1 : 0)
  )
}

export function scopeBeats(a: DemandScope, b: DemandScope): boolean {
  const sa = scopeSpecificity(a)
  const sb = scopeSpecificity(b)
  if (sa !== sb) return sa > sb
  return scopePriority(a) > scopePriority(b)
}

function sameScope(a: DemandScope, b: DemandScope): boolean {
  return a.channel === b.channel && a.warehouseId === b.warehouseId && a.marketId === b.marketId
}

export type CellEdit = {
  winningScope: DemandScope | undefined
  locked: boolean
  governedByPin: boolean
  manualBase: number
  edited: boolean
}

// What a typed number does in one cube cell. `scope` is the cell's own leaf-exact scope: an
// override AT it is the lock, anything broader merely governs the row.
export function resolveCellEdit(
  cell: { leaf: BaselineLeaf; scope: DemandScope; manual: number; manualAdjustment: number },
  overrideScopes: readonly DemandScope[],
): CellEdit {
  // Most-specific override matching this cell = the pin that set its base (leaf-exact = locked).
  let winningScope: DemandScope | undefined
  let locked = false
  // A broader override reaching this row makes typed edits land as locks, so rows keep summing
  // to the pin. Per row: an override matching no leaf must not flip every row's edit semantics.
  let governedByPin = false
  for (const scope of overrideScopes) {
    if (!leafMatchesScope(cell.leaf, scope)) continue
    if (sameScope(scope, cell.scope)) locked = true
    else governedByPin = true
    if (!winningScope || scopeBeats(scope, winningScope)) winningScope = scope
  }
  const delta = Math.round(cell.manualAdjustment)
  return {
    winningScope,
    locked,
    governedByPin,
    // `manual` includes the manual-stage delta; a replacement measures from the base under it.
    manualBase: Math.round(cell.manual) - delta,
    // The row's own add-delta only, not manual vs setupBase: base moves from assumptions or a
    // redistributing pin are the engine's doing, not a revertible edit.
    edited: delta !== 0,
  }
}

/** The running level after one row, in fold order; a row outside the leaf leaves it unchanged. */
export type FoldRun = { key: string | undefined; level: number }

/** One leaf's fold row by row: the levels the weekly attribution spreads at each run of a key. */
export type LeafFold = { setup: FoldRun[]; setupBase: number; manual: number; onTop: FoldRun[] }

// Seq order (pre-sorted by the caller): `%` compounds on the running value, `add` spreads by
// scope share. `attribution` collects each row's exact marginal by `key`: Σ over leaves is the
// row's units, Σ over rows the stage's net delta.
function foldAdjustments(
  projected: number,
  adjustments: DemandAdjustmentIntent[],
  leaf: BaselineLeaf,
  weights: ScopeWeights,
  attribution?: Map<string, number>,
  runs?: FoldRun[],
): number {
  for (const adj of adjustments) {
    if (leafMatchesScope(leaf, adj.scope)) {
      const marginal =
        adj.type === "percentage"
          ? projected * (adj.value / 100)
          : adj.value * scopeShare(leaf, adj.scope, weights)
      projected += marginal
      if (attribution && adj.key !== undefined)
        attribution.set(adj.key, (attribution.get(adj.key) ?? 0) + marginal)
    }
    runs?.push({ key: adj.key, level: projected })
  }
  return projected
}

const WHOLE_VARIANT_SCOPE: DemandScope = { channel: null, warehouseId: null, marketId: null }

// Insights are fixed units spread by scope share, so their order never matters.
function foldInsights(
  projected: number,
  insights: SetupInsightIntent[],
  leaf: BaselineLeaf,
  weights: ScopeWeights,
  attribution?: Map<string, number>,
  runs?: FoldRun[],
): number {
  for (const ins of insights) {
    if (leafMatchesScope(leaf, ins.scope)) {
      const shareScope = ins.spread === "variant" ? WHOLE_VARIANT_SCOPE : ins.scope
      const marginal = ins.value * scopeShare(leaf, shareScope, weights)
      projected += marginal
      if (attribution && ins.key !== undefined)
        attribution.set(ins.key, (attribution.get(ins.key) ?? 0) + marginal)
    }
    runs?.push({ key: ins.key, level: projected })
  }
  return projected
}

// Most-specific override wins each leaf; a broader one spreads its REMAINDER over unclaimed
// leaves by `leaf.level` (as `scopeShare`), so a leaf-exact override is a LOCK. A broader total
// always holds: locks that add up to more than it are scaled down to fit, keeping their split, so
// lowering How much? below its locks still takes effect. `shortfall` = the units scaled away, which
// callers surface. Cached on identity-stable keys; no caller may write to the result.
type Claims = {
  claimed: (number | undefined)[]
  /** The override that claimed each leaf. */
  by: (DemandOverrideIntent | undefined)[]
  shortfall: number
}
const claimsByLeaves = new WeakMap<BaselineLeaf[], WeakMap<DemandOverrideIntent[], Claims>>()

function claimOverrides(leaves: BaselineLeaf[], overrides: DemandOverrideIntent[]): Claims {
  if (overrides.length === 0)
    return {
      claimed: new Array<number | undefined>(leaves.length).fill(undefined),
      by: new Array<DemandOverrideIntent | undefined>(leaves.length).fill(undefined),
      shortfall: 0,
    }
  let byOverrides = claimsByLeaves.get(leaves)
  if (!byOverrides) claimsByLeaves.set(leaves, (byOverrides = new WeakMap()))
  const cached = byOverrides.get(overrides)
  if (cached) return cached
  const claimed = new Array<number | undefined>(leaves.length).fill(undefined)
  const by = new Array<DemandOverrideIntent | undefined>(leaves.length).fill(undefined)
  let shortfall = 0
  const sorted = [...overrides].sort((a, b) =>
    scopeBeats(a.scope, b.scope) ? -1 : scopeBeats(b.scope, a.scope) ? 1 : 0,
  )
  for (const o of sorted) {
    const open: number[] = []
    let lockedSum = 0
    for (let i = 0; i < leaves.length; i++) {
      if (!leafMatchesScope(leaves[i]!, o.scope)) continue
      const already = claimed[i]
      if (already !== undefined) lockedSum += already
      else open.push(i)
    }
    const excess = Math.max(0, lockedSum - o.total)
    shortfall += excess
    if (excess > 0) {
      const fit = o.total / lockedSum
      for (let i = 0; i < leaves.length; i++) {
        const already = claimed[i]
        if (already !== undefined && leafMatchesScope(leaves[i]!, o.scope))
          claimed[i] = already * fit
      }
    }
    if (open.length === 0) continue
    const remainder = Math.max(0, o.total - lockedSum)
    let denom = 0
    for (const i of open) denom += leaves[i]!.level
    for (const i of open) {
      claimed[i] = Math.max(0, remainder * shareOf(leaves[i]!.level, denom, open.length))
      by[i] = o
    }
  }
  const result = { claimed, by, shortfall }
  byOverrides.set(overrides, result)
  return result
}

/** The override each leaf's number comes from: the most specific one matching it. */
export function claimingOverrides(
  leaves: BaselineLeaf[],
  overrides: DemandOverrideIntent[],
): (DemandOverrideIntent | undefined)[] {
  return claimOverrides(leaves, overrides).by
}

/** The units each leaf's claiming override gives it; `undefined` where none claims. */
export function claimedOverrideUnits(
  leaves: BaselineLeaf[],
  overrides: DemandOverrideIntent[],
): (number | undefined)[] {
  return claimOverrides(leaves, overrides).claimed
}

export function overrideShortfall(
  leaves: BaselineLeaf[],
  overrides: DemandOverrideIntent[],
): number {
  return claimOverrides(leaves, overrides).shortfall
}

// Scoped rows matching no cell: stored but silently inert. Counted so callers can say so.
export function orphanedScopes(
  cells: BaselineLeaf[],
  rows: ReadonlyArray<{ scope: DemandScope }>,
): number {
  return rows.filter((r) => !cells.some((c) => leafMatchesScope(c, r.scope))).length
}

/**
 * `orphanedScopes` for windowed overlays: `target` decides which cells a row folds into, so a
 * pre-order row reaching the band (warehouse × channel, `marketId = ""`) is not inert. Windowless
 * rows fold at the leaf stage whatever their target, so they go through `orphanedScopes` instead.
 */
export function orphanedWindowedAdjustments(
  leaves: BaselineLeaf[],
  bandCells: BaselineLeaf[],
  windowed: ReadonlyArray<WindowedAdjustmentIntent>,
): number {
  return windowed.filter((a) => {
    const reachesSales = a.target !== "preorder" && leaves.some((l) => leafMatchesScope(l, a.scope))
    const reachesBand = a.target !== "sales" && bandCells.some((c) => leafMatchesScope(c, a.scope))
    return !reachesSales && !reachesBand
  }).length
}

// `onTopWindowless`: windowed on-top rows fold per week in `applyWindowedAdjustments` instead.
export type ProjectionLayers = {
  overrides: DemandOverrideIntent[]
  setupAdjustments: DemandAdjustmentIntent[]
  manualAdjustments: DemandAdjustmentIntent[]
  onTopWindowless: DemandAdjustmentIntent[]
  /** Fixed units folded FIRST: the base every strategy answer scales. */
  setupInsights: SetupInsightIntent[]
  /**
   * Cannibalization `%` rows from the Framing answers (`demandFramingSignal.ts`), folded after
   * model insights. Sized against Σ engine baseline, so a `full` answer lands covered variants on
   * target × (1 + insight effect), not on target.
   */
  framingAdjustments?: DemandAdjustmentIntent[]
  /**
   * Model-sized, planner-decided insights (`STRATEGIC_INSIGHT_KEYS`), folded after
   * Cannibalization and before the planner's setup rows, so a planner `%` compounds on them.
   */
  strategicInsights?: SetupInsightIntent[]
  /** Leaf-exact share pins (every axis set), applied to the Manual values before on-top. */
  sharePins?: DemandSharePinIntent[]
  /**
   * `floorToConfirmed` after the fold; default on. Off for the model stage, so the floor counts
   * after the signals that can clear it instead of before them.
   */
  applyPreorderFloor?: boolean
}

/**
 * Exact per-row units for the leaves `inScope` admits, one map per stack shown as a column.
 * `onTop` is only the leaf-stage half: windowed rows and the pre-order uplift attribute into it
 * from their own call sites.
 */
export type StackAttribution = {
  setup?: Map<string, number>
  onTop?: Map<string, number>
  /** Default: every leaf. */
  inScope?: (leaf: BaselineLeaf) => boolean
}

/** `folds`, when given, receives each leaf's fold in leaf order. */
export function projectDemandForVariant(
  leaves: BaselineLeaf[],
  layers: ProjectionLayers,
  attribution?: StackAttribution,
  folds?: LeafFold[],
): ProjectedLeaf[] {
  const {
    overrides,
    setupAdjustments,
    manualAdjustments,
    onTopWindowless,
    setupInsights,
    framingAdjustments = [],
    strategicInsights = [],
    sharePins = [],
    applyPreorderFloor = true,
  } = layers
  const { claimed, by } = claimOverrides(leaves, overrides)
  const weights = scopeWeightsFor(leaves)

  const stages = leaves.map((leaf, i) => {
    const baseline = leaf.level
    const override = claimed[i]
    const attributed = attribution && (attribution.inScope?.(leaf) ?? true)
    const setupInto = attributed ? attribution.setup : undefined
    const onTopInto = attributed ? attribution.onTop : undefined
    const fold = folds && { setup: [] as FoldRun[], onTop: [] as FoldRun[] }
    // Fixed setup order: model insights, Cannibalization `%`, comparable-season growth, then the
    // planner's setup rows. An override then REPLACES this base; setup is not re-applied on top.
    let setupBase = foldInsights(baseline, setupInsights, leaf, weights, setupInto, fold?.setup)
    setupBase = foldAdjustments(
      setupBase,
      framingAdjustments,
      leaf,
      weights,
      setupInto,
      fold?.setup,
    )
    setupBase = foldInsights(setupBase, strategicInsights, leaf, weights, setupInto, fold?.setup)
    setupBase = foldAdjustments(setupBase, setupAdjustments, leaf, weights, setupInto, fold?.setup)
    // Manual-stage rows are deltas on the pin (or setup base) that claim nothing, so siblings don't
    // absorb them. On-top stacks after.
    const manual = foldAdjustments(override ?? setupBase, manualAdjustments, leaf, weights)
    return { leaf, baseline, setupBase, manual, onTopInto, fold }
  })
  const manuals = applySharePins(
    stages.map((st) => st.manual),
    leaves,
    sharePins,
    by.map((o) => o != null && isLeafScope(o.scope)),
  )
  const folded = stages.map(({ leaf, baseline, setupBase, onTopInto, fold }, i) => {
    const manual = manuals[i]!
    const projected = foldAdjustments(
      manual,
      onTopWindowless,
      leaf,
      weights,
      onTopInto,
      fold?.onTop,
    )
    if (fold) folds?.push({ ...fold, setupBase, manual })
    return { ...leaf, baseline, projected: Math.max(0, projected) }
  })
  if (!applyPreorderFloor) return folded
  const floored = floorToConfirmed(
    folded.map((f) => f.projected),
    leaves,
  )
  const setupInto = attribution?.setup
  if (setupInto) {
    let moved = 0
    folded.forEach((f, i) => {
      if (attribution?.inScope?.(f) ?? true) moved += (floored[i] ?? f.projected) - f.projected
    })
    if (moved !== 0)
      setupInto.set(PREORDER_FLOOR_KEY, (setupInto.get(PREORDER_FLOOR_KEY) ?? 0) + moved)
  }
  return folded.map((f, i) => ({ ...f, projected: floored[i] ?? f.projected }))
}

/**
 * Share-pinned cells take their share of the Manual total; locked cells hold; the free cells scale
 * to the rest, so the total holds. Pins over the room left are scaled down to fit, and with no
 * free cell the pins scale to fill it.
 */
export function applySharePins(
  manual: readonly number[],
  leaves: readonly BaselineLeaf[],
  sharePins: readonly DemandSharePinIntent[],
  locked: readonly boolean[],
): number[] {
  if (sharePins.length === 0) return [...manual]
  const shareByCell = new Map(sharePins.map((p) => [cellKey(scopeToColumns(p.scope)), p.share]))
  const shares = leaves.map((l) => shareByCell.get(cellKey(l)))
  if (shares.every((s) => s == null)) return [...manual]
  const total = sumArray([...manual])
  const isLocked = (i: number) => shares[i] == null && locked[i]
  const isFree = (i: number) => shares[i] == null && !locked[i]
  let room = total
  let pinned = 0
  let free = 0
  let freeLevel = 0
  let freeCount = 0
  manual.forEach((m, i) => {
    if (isLocked(i)) room -= m
    else if (shares[i] != null) pinned += shares[i]! * total
    else {
      free += m
      freeLevel += leaves[i]!.level
      freeCount += 1
    }
  })
  room = Math.max(0, room)
  const fit = pinned > room || (freeCount === 0 && pinned > 0) ? room / pinned : 1
  const rest = Math.max(0, room - pinned * fit)
  return manual.map((m, i) => {
    if (shares[i] != null) return shares[i]! * total * fit
    if (!isFree(i)) return m
    if (free > 0) return (m * rest) / free
    return rest * shareOf(leaves[i]!.level, freeLevel, freeCount)
  })
}

/** Attribution key for the units `floorToConfirmed` moved, in the Setup stack. */
export const PREORDER_FLOOR_KEY = "floor:confirmedPreorders"

/**
 * Raises every floored cell to its confirmed pre-orders. The units come from the cells above their
 * floor in proportion to their headroom, so the total holds; what the headroom can't cover lifts
 * the total, unless the group's cells say `floorKeepsTotal`. A floor only moves units inside one
 * `floorGroup`, so a pin's total holds and a locked cell only ever lifts.
 */
export function floorToConfirmed(
  totals: readonly number[],
  leaves: readonly BaselineLeaf[],
): number[] {
  const out = [...totals]
  if (!leaves.some((l) => (l.preorderFloor ?? 0) > 0)) return out
  const groups = new Map<string, number[]>()
  leaves.forEach((l, i) => {
    const key = l.floorGroup ?? ""
    groups.set(key, [...(groups.get(key) ?? []), i])
  })
  for (const idxs of groups.values()) {
    const floorOf = (i: number) => Math.max(0, leaves[i]?.preorderFloor ?? 0)
    let deficit = 0
    let headroom = 0
    for (const i of idxs) {
      const x = totals[i] ?? 0
      deficit += Math.max(0, floorOf(i) - x)
      headroom += Math.max(0, x - floorOf(i))
    }
    if (deficit <= 0) continue
    if (deficit > headroom + 1e-9 && idxs.some((i) => leaves[i]?.floorKeepsTotal)) continue
    // Headroom pays first; whatever it can't cover is a lift, so the group total grows by it.
    const taken = headroom > 0 ? Math.min(1, deficit / headroom) : 0
    for (const i of idxs) {
      const x = totals[i] ?? 0
      const f = floorOf(i)
      out[i] = x < f ? f : x - taken * (x - f)
    }
  }
  return out
}

// Stored as `DemandAdjustment.target`; `Target*` means the planner's numeric goal elsewhere.
// Renaming the column must also rename it on `DemandAdjustment_history`, whose trigger copies
// column-for-column.
export type AdjustmentBand = "sales" | "preorder" | "both"

export type WindowedAdjustmentIntent = DemandAdjustmentIntent & {
  startDate: Date | null
  endDate: Date | null
  target: AdjustmentBand
  /** A `both` add's pre-order part; `value` is then its reorder part. */
  preorderValue?: number
}

// Absolute-date window → the 0-based horizon offsets it covers. Offset i is the week starting
// anchor + i weeks; included when that week-start falls within [startDate, endDate] inclusive.
// Both-null → empty set (those rows belong to the level stage, not here).
export function windowOffsets(
  startDate: Date | null,
  endDate: Date | null,
  horizonWeeks: number,
  anchor: Date,
): Set<number> {
  const out = new Set<number>()
  if (startDate == null || endDate == null) return out
  const start = startDate.getTime()
  const end = endDate.getTime()
  const base = anchor.getTime()
  for (let i = 0; i < horizonWeeks; i++) {
    const t = base + i * WEEK_MS
    if (t >= start && t <= end) out.add(i)
  }
  return out
}

// Last day a window covers: `windowOffsets` includes the week starting at `endDate`'s week
// start, so coverage runs to that Sunday. Display uses this, since the picker takes Mondays.
export function windowCoverageEnd(endDate: Date): Date {
  return new Date(convertToStartOfWeekUTC(endDate).getTime() + WEEK_MS - DAY_MS)
}

// Windowed rows only. Each computes off the pre-windowed `base` (non-compounding) and changes
// the total rather than redistributing. `base[i].weekly` must already include level-stage
// adjustments, seasonality and dated pins. With nothing windowed the returned `weekly` arrays
// ALIAS the caller's; skipping the clamp then is safe since every producer floors at 0.
export function applyWindowedAdjustments(
  base: ReadonlyArray<{ leaf: BaselineLeaf; weekly: number[] }>,
  adjustments: ReadonlyArray<WindowedAdjustmentIntent>,
  anchor: Date,
  // See `StackAttribution`; non-compounding, so a row's units are just what it wrote.
  attribution?: { into: Map<string, number>; inScope?: (leaf: BaselineLeaf) => boolean },
  /**
   * Index-aligned with `base`: what an `add` weighs its cells by when `base` has no weight in the
   * window, before falling back to uniform.
   */
  fallbackWeights?: ReadonlyArray<readonly number[]>,
): Array<{ leaf: BaselineLeaf; weekly: number[] }> {
  if (!adjustments.some((a) => a.startDate != null && a.endDate != null)) return [...base]

  const out = base.map((b) => ({ leaf: b.leaf, weekly: [...b.weekly] }))
  const horizon = base[0]?.weekly.length ?? 0
  const credit = (key: string | undefined, leaf: BaselineLeaf, units: number) => {
    if (!attribution || key === undefined) return
    if (!(attribution.inScope?.(leaf) ?? true)) return
    attribution.into.set(key, (attribution.into.get(key) ?? 0) + units)
  }

  for (const adj of adjustments) {
    if (adj.startDate == null || adj.endDate == null) continue
    const offsets = windowOffsets(adj.startDate, adj.endDate, horizon, anchor)
    if (offsets.size === 0) continue

    if (adj.type === "percentage") {
      base.forEach((b, idx) => {
        if (!leafMatchesScope(b.leaf, adj.scope)) return
        const o = out[idx]
        if (!o) return
        offsets.forEach((i) => {
          const marginal = (b.weekly[i] ?? 0) * (adj.value / 100)
          o.weekly[i] = (o.weekly[i] ?? 0) + marginal
          credit(adj.key, b.leaf, marginal)
        })
      })
    } else {
      // add: distribute `value` across matching (leaf, window-week) cells by their base share.
      const cells: { idx: number; i: number; weight: number }[] = []
      let denom = 0
      base.forEach((b, idx) => {
        if (!leafMatchesScope(b.leaf, adj.scope)) return
        offsets.forEach((i) => {
          const w = b.weekly[i] ?? 0
          cells.push({ idx, i, weight: w })
          denom += w
        })
      })
      if (cells.length === 0) continue
      if (denom <= 0 && fallbackWeights) {
        for (const c of cells) {
          c.weight = fallbackWeights[c.idx]?.[c.i] ?? 0
          denom += c.weight
        }
      }
      for (const c of cells) {
        const o = out[c.idx]
        if (!o) continue
        const marginal = adj.value * shareOf(c.weight, denom, cells.length)
        o.weekly[c.i] = (o.weekly[c.i] ?? 0) + marginal
        credit(adj.key, o.leaf, marginal)
      }
    }
  }

  for (const o of out) {
    for (let i = 0; i < o.weekly.length; i++) o.weekly[i] = Math.max(0, o.weekly[i] ?? 0)
  }
  return out
}
